import type { ReactiveControllerHost } from 'lit'

import { wrap } from '../number.js'

export type Orientation = 'vertical' | 'horizontal' | 'both'

export interface RovingFocusOptions {
  /** The focusable items, in DOM order. */
  items: () => HTMLElement[]
  /** Which arrow keys move focus. Defaults to `vertical`. */
  orientation?: () => Orientation
  /** Wrap focus at the ends. Defaults to `true`. */
  loop?: () => boolean
  /** Flips the horizontal arrow direction. Defaults to `false`. */
  isRTL?: () => boolean
  /** Whether an item can receive focus (skip disabled ones). Defaults to all. */
  isFocusable?: (item: HTMLElement) => boolean
}

/**
 * Manages arrow-key focus movement across a set of items (the shared mechanics
 * behind Base UI's `Composite`). The host calls {@link handleKeydown} from its
 * own `keydown`; the controller moves real DOM focus to the next/previous
 * focusable item, honouring orientation, looping, RTL and Home/End.
 */
export class RovingFocusController {
  // A host-bound helper rather than a lifecycle ReactiveController: it moves real
  // DOM focus on demand and owns no connect/update hooks, so it doesn't register
  // via `addController`. The host reference is kept for parity with the other
  // controllers and any future host-driven behaviour.
  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: RovingFocusOptions,
  ) {}

  /** Handle a navigation keydown. Returns `true` if it moved focus. */
  handleKeydown(event: KeyboardEvent): boolean {
    const orientation = this.options.orientation?.() ?? 'vertical'
    const vertical = orientation === 'vertical' || orientation === 'both'
    const horizontal = orientation === 'horizontal' || orientation === 'both'
    const rtl = this.options.isRTL?.() ?? false

    switch (event.key) {
      case 'ArrowDown':
        return vertical ? this.move(1, event) : false
      case 'ArrowUp':
        return vertical ? this.move(-1, event) : false
      case 'ArrowRight':
        return horizontal ? this.move(rtl ? -1 : 1, event) : false
      case 'ArrowLeft':
        return horizontal ? this.move(rtl ? 1 : -1, event) : false
      case 'Home':
        event.preventDefault()
        this.focusFirst()
        return true
      case 'End':
        event.preventDefault()
        this.focusLast()
        return true
      default:
        return false
    }
  }

  /** Focus the first focusable item. */
  focusFirst() {
    const items = this.options.items()
    const index = this.nextFocusable(items, 0, 1, false)
    if (index !== -1) {
      items[index].focus()
    }
  }

  /** Focus the last focusable item. */
  focusLast() {
    const items = this.options.items()
    const index = this.nextFocusable(items, items.length - 1, -1, false)
    if (index !== -1) {
      items[index].focus()
    }
  }

  private get loop(): boolean {
    return this.options.loop?.() ?? true
  }

  private isFocusable(item: HTMLElement): boolean {
    return this.options.isFocusable?.(item) ?? true
  }

  private move(direction: number, event: KeyboardEvent): boolean {
    const items = this.options.items()
    const length = items.length
    if (length === 0) {
      return false
    }
    const from = items.indexOf(document.activeElement as HTMLElement)
    // With nothing focused, start from the matching end; otherwise step off the
    // current item.
    const start = from < 0 ? (direction > 0 ? 0 : length - 1) : from + direction
    const target = this.nextFocusable(items, start, direction, this.loop)
    if (target === -1) {
      return false
    }
    event.preventDefault()
    items[target].focus()
    return true
  }

  /**
   * The next focusable item from `start` (checked first), stepping by `direction`
   * and skipping non-focusable items, wrapping at the ends when `loop`. Returns
   * `-1` if none is found.
   */
  private nextFocusable(items: HTMLElement[], start: number, direction: number, loop: boolean): number {
    const length = items.length
    if (length === 0) {
      return -1
    }

    let index = start
    if (index < 0 || index >= length) {
      if (!loop) {
        return -1
      }
      index = wrap(index, 0, length - 1)
    }

    for (let i = 0; i < length; i++) {
      if (this.isFocusable(items[index])) {
        return index
      }
      const stepped = index + direction
      if (stepped < 0 || stepped >= length) {
        if (!loop) {
          return -1
        }
        index = wrap(stepped, 0, length - 1)
      }
      else {
        index = stepped
      }
    }
    return -1
  }
}
